import React from 'react';
import {
  Box, Container, Card, CardContent, Typography, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Chip, CircularProgress, Alert, Paper
} from '@mui/material';
import {
  Business as BusinessIcon, CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon, Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import apiClient from '../../request/http-common';

const MyJobPostings = () => {
  const { data, isLoading, error } = useQuery(
    'myJobPostings',
    async () => {
      const response = await apiClient.get(`/api/user/job-postings`);
      return response.data;
    },
    {
      refetchInterval: 30000,
    }
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load job postings
        </Alert>
      </Container>
    );
  }

  const jobs = data?.jobs || [];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon sx={{ color: '#10B981' }} />;
      case 'rejected':
        return <CancelIcon sx={{ color: '#EF4444' }} />;
      default:
        return <ScheduleIcon sx={{ color: '#F59E0B' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)', border: '1px solid #2B3139' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            My Job Postings
          </Typography>
          <Typography variant="body2" sx={{ color: '#B7BDC6' }}>
            Manage and track all your job postings
          </Typography>
        </Box>

        {jobs.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6, background: 'transparent', boxShadow: 'none' }}>
            <CardContent>
              <BusinessIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#B7BDC6', mb: 1 }}>
                No job postings found
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                You haven't posted any jobs yet. Create your first job posting now!
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <List>
            {jobs.map((job) => (
              <ListItem
                key={job.id}
                sx={{
                  border: '1px solid #2B3139',
                  borderRadius: 2,
                  mb: 2,
                  background: 'linear-gradient(135deg, #1E2328 0%, #161A1E 100%)',
                  '&:hover': {
                    borderColor: '#F7931A',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#F7931A', width: 56, height: 56 }}>
                    {getStatusIcon(job.status)}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {job.job_title}
                      </Typography>
                      <Chip
                        label={job.company_name}
                        size="small"
                        sx={{ background: 'rgba(247, 147, 26, 0.2)', color: '#F7931A', fontWeight: 600 }}
                      />
                      <Chip
                        label={job.status.toUpperCase()}
                        size="small"
                        color={getStatusColor(job.status)}
                        sx={{ fontWeight: 600 }}
                      />
                      {job.job_type && (
                        <Chip
                          label={job.job_type}
                          size="small"
                          sx={{ background: 'rgba(2, 192, 118, 0.2)', color: '#02C076', fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" sx={{ color: '#B7BDC6', mt: 0.5 }}>
                        <strong>Location:</strong> {job.location || 'Not specified'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#B7BDC6', mt: 0.5 }}>
                        <strong>Salary:</strong> {job.salary_range || 'Not specified'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#B7BDC6', mt: 0.5 }}>
                        <strong>Posted:</strong> {new Date(job.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </Typography>
                      {job.processed_at && (
                        <Typography variant="body2" sx={{ color: '#B7BDC6', mt: 0.5 }}>
                          <strong>Processed:</strong> {new Date(job.processed_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </Typography>
                      )}
                      {job.admin_notes && (
                        <Box sx={{ mt: 1, p: 1.5, background: 'rgba(247, 147, 26, 0.1)', borderRadius: 1, border: '1px solid rgba(247, 147, 26, 0.3)' }}>
                          <Typography variant="body2" sx={{ color: '#F7931A', fontWeight: 600, mb: 0.5 }}>
                            Admin Note:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#B7BDC6' }}>
                            {job.admin_notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default MyJobPostings;

